import { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { ApplicationRole } from "../../../api/models/ApplicationRole";
import { UsersService } from "../../../api/services/UsersService";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Paper from "@mui/material/Paper";
import FormHelperText from "@mui/material/FormHelperText";
import { CalendarEventViewModel } from "../../../api/models/CalendarEventViewModel";
import dayjs from "dayjs";
import "./Attorneys.scss";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { UserShortResponse } from "../../../api/models/UserShortResponse";
import { useSelector } from "react-redux";
import { isEmpty } from "../../../services/Helpers";
import { Category, GetUserListRequest } from "../../../api";
import {
  AppButton,
  FormField,
  FormSection,
  PageHeader,
  toast,
} from "../../../components/ui";

const eventsColors = [
  "#878787",
  "#A0CFBF",
  "#A294CE",
  "#B6B020",
  "#AE9685",
  "#7488A0",
  "#6CB245",
];

export default function ReviewAssignmentCalendar() {
  const [staffEmpAttorneysList, setStaffEmpAttorneysList] = useState<
    UserShortResponse[]
  >([]);
  const [selectedEmpStaffAttorneyId, setSelectedEmpStaffAttorneyId] =
    useState<number>();

  const [staffPIAttorneysList, setStaffPIAttorneysList] = useState<
    UserShortResponse[]
  >([]);
  const [selectedPIStaffAttorneyId, setSelectedPIStaffAttorneyId] =
    useState<number>();

  const [eventEditId, setEventEditId] = useState<number | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [dateStart, setDateStart] = useState<string | null>();
  const [dateEnd, setDateEnd] = useState<string | null>();
  const [dateHasEvent, setDateHasEvent] = useState(false);

  const user = useSelector((state: any) => state.user?.user);

  useEffect(() => {
    if (isEmpty(user)) {
      return;
    }

    if (user.roles?.includes(ApplicationRole.ADMIN)) {
      UsersService.postApiUsersGetUsersByRole({
        role: ApplicationRole.STAFF_ATTORNEY,
        category: Category.EMPLOYMENT,
      } as GetUserListRequest).then((attorneys) => {
        setStaffEmpAttorneysList(attorneys);
      });

      UsersService.postApiUsersGetUsersByRole({
        role: ApplicationRole.STAFF_ATTORNEY,
        category: Category.PERSONAL_INJURY,
      } as GetUserListRequest).then((attorneys) => {
        setStaffPIAttorneysList(attorneys);
        const current = attorneys.filter(
          (attorney) => attorney.isCurrentReviewing
        );
        if (current.length === 1) {
          setSelectedPIStaffAttorneyId(current[0].id);
        }
      });
    }
  }, [user]);

  useEffect(() => {
    UsersService.getApiUsersGetCalendarEvents().then((loaded) => {
      const calendarEvents = loaded.map((event) => {
        const currentAttorneyIndex = staffEmpAttorneysList.findIndex(
          (attorney) => attorney.id === event.attorneyId
        );
        const color =
          eventsColors[
            currentAttorneyIndex < eventsColors.length
              ? currentAttorneyIndex
              : currentAttorneyIndex - eventsColors.length
          ];
        return {
          id: event.id,
          attorneyId: event.attorneyId,
          title: event.title,
          start: event.start?.slice(0, 10),
          end: event.end?.slice(0, 10),
          backgroundColor: color,
          borderColor: color,
        };
      });
      setEvents(calendarEvents);
    });
  }, [staffEmpAttorneysList]);

  const handleDateClick = (date: { dateStr: string }) => {
    resetValues();
    setDateStart(date.dateStr);
  };

  const handleDateSelection = (date: { startStr: string; endStr: string }) => {
    resetValues();
    setDateStart(date.startStr);
    setDateEnd(date.endStr);
  };

  const handleEventSave = () => {
    if (checkDatesExist(dateStart, dateEnd ?? dateStart)) {
      return;
    }
    const currentAttorneyIndex = staffEmpAttorneysList.findIndex(
      (attorney) => attorney.id === selectedEmpStaffAttorneyId
    );
    const color =
      eventsColors[
        currentAttorneyIndex < eventsColors.length
          ? currentAttorneyIndex
          : currentAttorneyIndex - eventsColors.length
      ];
    const event = {
      id:
        eventEditId ??
        Math.max(...events.map((e) => e.id as number), 0) + 1,
      attorneyId: selectedEmpStaffAttorneyId,
      title: `${staffEmpAttorneysList[currentAttorneyIndex]?.firstName} ${staffEmpAttorneysList[currentAttorneyIndex]?.lastName}`,
      start: dateStart,
      end: dayjs(dateEnd ?? dateStart).add(1, "day").format("YYYY-MM-DD"),
      backgroundColor: color,
      borderColor: color,
    };
    if (eventEditId) {
      UsersService.putApiUsersUpdateCalendarEvent({
        ...event,
        end: dayjs(dateEnd ?? dateStart).add(1, "day").format("YYYY-MM-DD"),
      } as CalendarEventViewModel)
        .then(() => {
          const newEvents = [...events];
          const eventIndex = newEvents.findIndex((e) => e.id === eventEditId);
          newEvents[eventIndex] = event;
          setEvents(newEvents);
          resetValues();
          toast.success("Schedule updated.");
        })
        .catch(() => {
          toast.error("Error updating schedule.");
        });
      return;
    }
    setEvents((prevEvents) => [...prevEvents, event]);
    UsersService.postApiUsersAddCalendarEvents([
      {
        ...event,
        end: dayjs(dateEnd ?? dateStart).add(1, "day").format("YYYY-MM-DD"),
      } as CalendarEventViewModel,
    ])
      .then(() => {
        resetValues();
        toast.success("Schedule created.");
      })
      .catch(() => {
        toast.error("Error updating schedule.");
      });
  };

  const checkDatesExist = (start: string | null | undefined, end: string | null | undefined) => {
    const selectedDayEventExist = events.find((event) => {
      const eventEnd =
        event.start !== event.end
          ? dayjs(event.end).subtract(1, "day")
          : dayjs(event.end);
      return (
        event.id !== eventEditId &&
        ((dayjs(event.start) <= dayjs(start) && eventEnd >= dayjs(start)) ||
          (dayjs(event.start) <= dayjs(end) && eventEnd >= dayjs(end)) ||
          (dayjs(event.start) >= dayjs(start) && eventEnd <= dayjs(end)))
      );
    });
    setDateHasEvent(!!selectedDayEventExist);
    return !!selectedDayEventExist;
  };

  const handleEventClick = (event: any) => {
    setSelectedEmpStaffAttorneyId(event.event._def.extendedProps.attorneyId);
    setDateStart(dayjs(event.event._instance.range.start).format("YYYY-MM-DD"));
    setDateEnd(
      dayjs(event.event._instance.range.end)
        .subtract(1, "day")
        .format("YYYY-MM-DD")
    );
    setEventEditId(+event.event._def.publicId);
    setDateHasEvent(false);
  };

  const handleEventChange = (event: any) => {
    const eventIndex = events.findIndex(
      (e) => e.id === +event.event._def.publicId
    );
    const oldEvent = events[eventIndex];
    const newEvent = {
      id: +event.event._def.publicId,
      title: oldEvent.title,
      backgroundColor: oldEvent.backgroundColor,
      borderColor: oldEvent.borderColor,
      attorneyId: event.event._def.extendedProps.attorneyId,
      start: dayjs(event.event._instance.range.start).format("YYYY-MM-DD"),
      end:
        dayjs(event.event._instance.range.end).format("YYYY-MM-DD") ??
        dayjs(event.event._instance.range.start).format("YYYY-MM-DD"),
    };
    UsersService.putApiUsersUpdateCalendarEvent({
      ...newEvent,
    } as CalendarEventViewModel)
      .then(() => {
        toast.success("Schedule updated.");
      })
      .catch(() => {
        toast.error("Error updating schedule.");
      });
    const newEvents = [...events];
    newEvents[eventIndex] = newEvent;
    setEvents(newEvents);
    resetValues();
  };

  const handleEventDelete = () => {
    UsersService.deleteApiUsersCalendarEvent(eventEditId!)
      .then(() => {
        const eventIndex = events.findIndex((e) => e.id === eventEditId);
        const newEvents = [...events];
        newEvents.splice(eventIndex, 1);
        setEvents(newEvents);
        resetValues();
        toast.success("Schedule deleted.");
      })
      .catch(() => {
        toast.error("Error updating schedule.");
      });
  };

  const handleUpdatePIReviewer = (id: number) => {
    setSelectedPIStaffAttorneyId(id);
    UsersService.putApiUsersSetReviewer(id)
      .then(() => {
        toast.success("Updated reviewing attorney.");
      })
      .catch(() => {
        toast.error("Could not update reviewing attorney.");
      });
  };

  const resetValues = () => {
    setEventEditId(null);
    setDateEnd(null);
    setDateStart(null);
    setDateHasEvent(false);
  };

  const missingRequired = !dateStart || !selectedEmpStaffAttorneyId;

  return (
    <Box sx={{ width: "100%", pb: 3 }}>
      <PageHeader
        title="Review schedule"
        subtitle="Assign attorneys to lead review coverage."
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(280px, 320px) minmax(0, 1fr)",
          },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Stack spacing={2}>
          <FormSection
            title="Employment schedule"
            subtitle={
              eventEditId
                ? "Edit the selected coverage block"
                : "Create a coverage block on the calendar"
            }
          >
            <Stack spacing={2}>
              <FormField
                select
                required
                label="Select attorney"
                value={selectedEmpStaffAttorneyId ?? ""}
                onChange={(e) =>
                  setSelectedEmpStaffAttorneyId(Number(e.target.value))
                }
              >
                {staffEmpAttorneysList.map((attorney) => (
                  <MenuItem key={attorney.id} value={attorney.id}>
                    {attorney.firstName} {attorney.lastName}
                  </MenuItem>
                ))}
              </FormField>

              <DatePicker
                label="Start date *"
                value={dateStart ? dayjs(dateStart) : null}
                onChange={(value) =>
                  setDateStart(value ? dayjs(value).format("YYYY-MM-DD") : null)
                }
                slotProps={{
                  textField: { size: "medium", fullWidth: true },
                }}
              />

              <DatePicker
                label="End date"
                value={dateEnd ? dayjs(dateEnd) : null}
                onChange={(value) =>
                  setDateEnd(value ? dayjs(value).format("YYYY-MM-DD") : null)
                }
                slotProps={{
                  textField: { size: "medium", fullWidth: true },
                }}
              />

              {(dateHasEvent || missingRequired) && (
                <Box>
                  {dateHasEvent && (
                    <FormHelperText error sx={{ m: 0 }}>
                      Attorney already selected inside this date range
                    </FormHelperText>
                  )}
                  {missingRequired && (
                    <FormHelperText error sx={{ m: 0, mt: dateHasEvent ? 0.5 : 0 }}>
                      Start date and attorney are required
                    </FormHelperText>
                  )}
                </Box>
              )}

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <AppButton
                  color="primary"
                  variant="contained"
                  disabled={!selectedEmpStaffAttorneyId}
                  onClick={handleEventSave}
                >
                  Save
                </AppButton>
                <AppButton
                  color="error"
                  variant="outlined"
                  disabled={!eventEditId}
                  onClick={handleEventDelete}
                >
                  Delete
                </AppButton>
              </Stack>
            </Stack>
          </FormSection>

          <FormSection
            title="PI reviewer"
            subtitle="Select the current personal injury attorney for review"
          >
            <FormField
              select
              label="Select attorney"
              value={selectedPIStaffAttorneyId ?? ""}
              onChange={(e) => handleUpdatePIReviewer(Number(e.target.value))}
            >
              {staffPIAttorneysList.map((attorney) => (
                <MenuItem key={attorney.id} value={attorney.id}>
                  {attorney.firstName} {attorney.lastName}
                </MenuItem>
              ))}
            </FormField>
          </FormSection>
        </Stack>

        <Paper
          variant="outlined"
          className="calendar-holder"
          sx={{
            p: { xs: 1.5, md: 2 },
            borderColor: "divider",
            boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "",
            }}
            buttonText={{
              today: "Today",
            }}
            events={events}
            selectable
            selectOverlap={false}
            editable
            eventOverlap={false}
            eventStartEditable
            eventResizableFromStart
            eventDisplay="block"
            dayMaxEvents={3}
            fixedWeekCount={false}
            height="auto"
            dateClick={handleDateClick}
            select={handleDateSelection}
            eventClick={handleEventClick}
            eventDrop={handleEventChange}
            eventResize={handleEventChange}
          />
        </Paper>
      </Box>
    </Box>
  );
}
